import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider, useToast } from './contexts/ToastContext';

// Views
import Login from './components/views/Login';
import Sales from './components/views/Sales';
import HarvestAgents from './components/views/HarvestAgents';
import Mortality from './components/views/Mortality';
import Employees from './components/views/Employees';
import UserManagement from './components/views/UserManagement';
import Locations from './components/views/Locations';
import DatabaseManagement from './components/views/DatabaseManagement';
import Sessions from './components/views/Sessions';
import UserProfile from './components/views/UserProfile';
import PaymentDetails from './components/views/PaymentDetails';

// Common Components
import SearchOverlay from './components/common/SearchOverlay';
import ProtectedRoute from './components/common/ProtectedRoute';
import AppHeader from './components/common/AppHeader';
import AppSidebar from './components/common/AppSidebar';
import HelpModal from './components/common/HelpModal';
import ImageLightbox from './components/common/ImageLightbox';

// Modals
import ViewSaleModal from './components/modals/ViewSaleModal';
import ViewHarvestAgentsModal from './components/modals/ViewHarvestAgentsModal';

// Utils
import { fetchWithAuth } from './utils/api';

// Wraps each route's contents in a consistent motion transition. The unique
// key on motion.div makes AnimatePresence track the page change so it can
// run exit animations when the route changes.
function RoutePage({ name, children }) {
  return (
    <motion.div
      key={name}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      style={{ height: '100%' }}
    >
      {children}
    </motion.div>
  );
}

function AppContent() {
  const { user, logout, hasPermission, loading } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  // Cross-route navigation targets: when a view opens a record from
  // elsewhere in the app, the originating handler stashes the id here and
  // the destination view consumes/clears it on mount.
  const [targetSaleId, setTargetSaleId] = useState(null);
  const [targetEmployeeName, setTargetEmployeeName] = useState('');
  const [targetSaleTally, setTargetSaleTally] = useState('');
  const [targetMortalityId, setTargetMortalityId] = useState(null);
  const [targetEmployeeId, setTargetEmployeeId] = useState(null);
  const [targetHarvestId, setTargetHarvestId] = useState(null);

  const [version, setVersion] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [sharedViewingSale, setSharedViewingSale] = useState(null);
  const [harvestAgentsTallyForModal, setHarvestAgentsTallyForModal] = useState('');
  const [viewImage, setViewImage] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 1024);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const clearAllTargets = useCallback(() => {
    setTargetSaleId(null);
    setTargetHarvestId(null);
    setTargetMortalityId(null);
    setTargetEmployeeId(null);
    setTargetEmployeeName('');
    setTargetSaleTally('');
  }, []);

  useEffect(() => {
    fetch('/version')
      .then(res => res.json())
      .then(data => setVersion(data.version))
      .catch(() => setVersion('unknown'));

    fetch('/config')
      .then(res => res.json())
      .then(data => setGoogleClientId(data.google_client_id))
      .catch(err => console.error("Failed to load config", err));

    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth > 1024);
    };
    window.addEventListener('resize', handleResize);

    const handleGlobalKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  // One AbortController per in-flight search: a response to an older query
  // must never overwrite results for a newer one. Without this, typing fast
  // raced (slow "ab" response landing after fast "abc" response), and every
  // keystroke kept a useless request alive.
  const searchAbortRef = useRef(null);

  const cancelPendingSearch = useCallback(() => {
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }
  }, []);

  // useCallback keeps the handler's identity stable: SearchOverlay's debounce
  // effect depends on it, so an unstable identity would re-arm the timer (and
  // re-fire the request) on every render — one query became a request chain.
  const handleSearch = useCallback((query) => {
    cancelPendingSearch();

    if (!query || query.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    const controller = new AbortController();
    searchAbortRef.current = controller;
    setSearchLoading(true);

    fetchWithAuth(`/search?q=${encodeURIComponent(query)}`, { signal: controller.signal }, user, logout)
      .then(res => res.json())
      .then(data => {
        // Ignore a response that lost the race against a newer keystroke.
        if (controller.signal.aborted) return;
        setSearchResults(data);
      })
      .catch(err => {
        if (err?.name !== 'AbortError') console.error("Search failed", err);
      })
      .finally(() => {
        // Only the newest request owns the spinner.
        if (!controller.signal.aborted) setSearchLoading(false);
      });
  }, [cancelPendingSearch, user, logout]);

  const handleSelectSearchResult = (result) => {
    closeSearch();
    clearAllTargets();

    switch (result.type) {
      case 'sale':
        setTargetSaleId(result.id);
        navigate('/sales');
        break;
      case 'harvest':
        setTargetHarvestId(result.id);
        navigate('/harvest_agents');
        break;
      case 'mortality':
        setTargetMortalityId(result.id);
        navigate('/mortality');
        break;
      case 'employee':
        setTargetEmployeeId(result.id);
        navigate('/employees');
        break;
      default:
        break;
    }
  };

  // Single-sale lookup: `/sales?id=N` returns just that record instead of
  // downloading the whole table (base64 tally-sheet images and all) to
  // .find() one row in the browser.
  const handleGlobalSaleLinkClick = (id) => {
    fetchWithAuth(`/sales?id=${encodeURIComponent(id)}`, {}, user, logout)
      .then(res => res.json())
      .then(sales => {
        const sale = Array.isArray(sales) ? sales.find(s => s.id === id) : null;
        if (sale) setSharedViewingSale(sale);
      })
      .catch(err => toast.error("Could not load sale: " + err.message));
  };

  // Closing the overlay also cancels whatever search is still in flight.
  const closeSearch = useCallback(() => {
    cancelPendingSearch();
    setIsSearchOpen(false);
  }, [cancelPendingSearch]);

  const handleSidebarNavClick = () => {
    clearAllTargets();
    if (window.innerWidth <= 1024) setIsSidebarOpen(false);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <div className="App">
        {user && (
          <AppHeader
            user={user}
            version={version}
            onSidebarToggle={() => setIsSidebarOpen(open => !open)}
            onSearchOpen={() => setIsSearchOpen(true)}
            onProfileClick={() => navigate('/profile')}
            onHelpOpen={() => setIsHelpModalOpen(true)}
            onLogout={logout}
          />
        )}

        <div className="app-container">
          {user && (
            <AppSidebar
              user={user}
              hasPermission={hasPermission}
              isOpen={isSidebarOpen}
              onNavClick={handleSidebarNavClick}
            />
          )}

          {user && isSidebarOpen && window.innerWidth <= 1024 && (
            <div className="modal sidebar-backdrop" onClick={() => setIsSidebarOpen(false)} />
          )}

          <main className={user ? "app-main" : ""}>
            <AnimatePresence mode="wait">
              <Routes>
                <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login version={version} googleClientId={googleClientId} />} />

                <Route path="/sales" element={
                  <ProtectedRoute permission="sales">
                    <RoutePage name="sales">
                      <Sales
                        user={user}
                        targetSaleId={targetSaleId}
                        clearTargetSale={() => setTargetSaleId(null)}
                        onSaleLinkClick={handleGlobalSaleLinkClick}
                        onViewHarvests={(tally) => setHarvestAgentsTallyForModal(tally)}
                        handleLogout={logout}
                      />
                    </RoutePage>
                  </ProtectedRoute>
                } />

                <Route path="/harvest_agents" element={
                  <ProtectedRoute permission="harvest_agents">
                    <RoutePage name="harvest_agents">
                      <HarvestAgents
                        user={user}
                        targetEmployeeName={targetEmployeeName}
                        clearTargetEmployee={() => setTargetEmployeeName('')}
                        targetSaleTally={targetSaleTally}
                        clearTargetSaleTally={() => setTargetSaleTally('')}
                        targetHarvestId={targetHarvestId}
                        clearTargetHarvest={() => setTargetHarvestId(null)}
                        onSaleLinkClick={handleGlobalSaleLinkClick}
                        handleLogout={logout}
                      />
                    </RoutePage>
                  </ProtectedRoute>
                } />

                <Route path="/mortality" element={
                  <ProtectedRoute permission="mortality">
                    <RoutePage name="mortality">
                      <Mortality
                        user={user}
                        targetMortalityId={targetMortalityId}
                        clearTargetMortality={() => setTargetMortalityId(null)}
                        handleLogout={logout}
                      />
                    </RoutePage>
                  </ProtectedRoute>
                } />

                <Route path="/employees" element={
                  <ProtectedRoute permission="employees">
                    <RoutePage name="employees">
                      <Employees
                        user={user}
                        targetEmployeeId={targetEmployeeId}
                        clearTargetEmployee={() => setTargetEmployeeId(null)}
                        onEmployeeLinkClick={(name) => { setTargetEmployeeName(name); navigate('/harvest_agents'); }}
                        handleLogout={logout}
                      />
                    </RoutePage>
                  </ProtectedRoute>
                } />

                <Route path="/users" element={
                  <ProtectedRoute>
                    <RoutePage name="users">
                      {user?.role === 'admin' ? <UserManagement user={user} handleLogout={logout} /> : <Navigate to="/" replace />}
                    </RoutePage>
                  </ProtectedRoute>
                } />

                <Route path="/locations" element={
                  <ProtectedRoute>
                    <RoutePage name="locations">
                      {user?.role === 'admin' ? <Locations user={user} handleLogout={logout} /> : <Navigate to="/" replace />}
                    </RoutePage>
                  </ProtectedRoute>
                } />

                <Route path="/payment_details" element={
                  <ProtectedRoute>
                    <RoutePage name="payment_details">
                      {user?.role === 'admin' ? <PaymentDetails user={user} handleLogout={logout} /> : <Navigate to="/" replace />}
                    </RoutePage>
                  </ProtectedRoute>
                } />

                <Route path="/database" element={
                  <ProtectedRoute>
                    <RoutePage name="database">
                      {user?.role === 'admin' ? <DatabaseManagement user={user} handleLogout={logout} /> : <Navigate to="/" replace />}
                    </RoutePage>
                  </ProtectedRoute>
                } />

                <Route path="/sessions" element={
                  <ProtectedRoute>
                    <RoutePage name="sessions">
                      {user?.role === 'admin' ? <Sessions user={user} handleLogout={logout} /> : <Navigate to="/" replace />}
                    </RoutePage>
                  </ProtectedRoute>
                } />

                <Route path="/profile" element={
                  <ProtectedRoute>
                    <RoutePage name="profile">
                      <UserProfile user={user} handleLogout={logout} />
                    </RoutePage>
                  </ProtectedRoute>
                } />

                <Route path="/" element={<Navigate to="/sales" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AnimatePresence>
          </main>
        </div>

        <ViewSaleModal
          sale={sharedViewingSale}
          onClose={() => setSharedViewingSale(null)}
          setViewImage={setViewImage}
          onViewHarvests={(tally) => {
            setSharedViewingSale(null);
            setHarvestAgentsTallyForModal(tally);
          }}
          onEdit={(sale) => {
            setSharedViewingSale(null);
            setTargetSaleId(sale.id);
            navigate('/sales');
          }}
        />

        <ViewHarvestAgentsModal
          tally={harvestAgentsTallyForModal}
          onClose={() => setHarvestAgentsTallyForModal('')}
          onShowInPage={(tally) => {
            setTargetSaleTally(tally);
            navigate('/harvest_agents');
            setHarvestAgentsTallyForModal('');
          }}
          user={user}
          handleLogout={logout}
        />

        <AnimatePresence>
          {isSearchOpen && (
            <SearchOverlay
              onClose={closeSearch}
              onSearch={handleSearch}
              results={searchResults}
              loading={searchLoading}
              onSelect={handleSelectSearchResult}
            />
          )}
        </AnimatePresence>

        <HelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />

        <ImageLightbox src={viewImage} onClose={() => setViewImage(null)} />
      </div>
    </GoogleOAuthProvider>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
